import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@bb-ui/react-library/dist/components/Dialog';
import { DialogTitle } from '@bb-ui/react-library/dist/components/DialogTitle';
import { DialogContent } from '@bb-ui/react-library/dist/components/DialogContent';
import { DialogActions } from '@bb-ui/react-library/dist/components/DialogActions';
import {
    DefaultButton,
    PrimaryButton,
} from '@bb-ui/react-library/dist/components/Button';
import { TextField } from '@bb-ui/react-library/dist/components/TextField';
import { InputAdornment } from '@bb-ui/react-library/dist/components/InputAdornment';
import { useSnackbar } from '@bb-ui/react-library';
import {
    useMediaQuery,
    useTheme,
} from '@bb-ui/react-library/dist/components/styles';
import CircularProgress from '@material-ui/core/CircularProgress/CircularProgress';
import { useApi } from '../../../../hooks/useApi';
import { ApiFailureDialogContent } from '../../../ApiFailureDialogContent/ApiFailureDialogContent';
import { useStyles } from './CreateServiceAccountDialog.styles';

/**
 * What the create endpoint actually returns. open_api.yaml marks only tenantId
 * and username as required on AddServiceUserResponse - the status and expiry
 * fields are derived on the read path and are absent here. It is a success
 * signal, never a row to render, so the caller refetches the list instead.
 */
interface CreateServiceAccountResponse {
    tenantId: string;
    username: string;
}

/**
 * The backend adds the prefix itself. The user types only the suffix, and the
 * UI shows the prefix in front so users can see the final name.
 */
const SERVICE_ACCOUNT_PREFIX = 'SVC_BLACKBOARD_DATA_';

const MAX_SUFFIX_LENGTH = 50;
const SUFFIX_PATTERN = /^[A-Za-z0-9]+$/;

interface CreateServiceAccountDialogProps {
    open: boolean;
    serviceAccountsBaseUrl: string;
    onClose: () => void;
    onCreated: () => void;
}

export const CreateServiceAccountDialog: React.FC<
    CreateServiceAccountDialogProps
> = ({ open, serviceAccountsBaseUrl, onClose, onCreated }) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const classes = useStyles();
    const theme = useTheme();
    // On a phone prefix fills most of the input, so customers can barely see what they type.
    // so dropping prefix on mobile (the preview below still shows the full name).
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

    const [suffix, setSuffix] = React.useState('');
    const [fieldError, setFieldError] = React.useState('');
    const [submitted, setSubmitted] = React.useState(false);
    // Gate the failure-content swap on "user has tried at least once" so the
    // form isn't hidden by a pre-existing error.
    const [triedSubmit, setTriedSubmit] = React.useState(false);

    const apiConfig = React.useMemo(
        () => ({
            url: serviceAccountsBaseUrl,
            method: 'POST' as const,
            useAuth: true,
            executeType: 'manual' as const,
            data: { nameSuffix: suffix },
        }),
        [serviceAccountsBaseUrl, suffix],
    );

    const {
        data: createdAccount,
        loading,
        error,
        execute,
    } = useApi<CreateServiceAccountResponse>(apiConfig);

    const handleClose = React.useCallback(() => {
        setSuffix('');
        setFieldError('');
        setSubmitted(false);
        setTriedSubmit(false);
        onClose();
    }, [onClose]);

    React.useEffect(() => {
        if (!submitted || loading) return;
        if (createdAccount && !error) {
            enqueueSnackbar(
                t('settings.userManagement.create.snackbarSuccess'),
            );
            onCreated();
            handleClose();
        }
    }, [
        submitted,
        loading,
        createdAccount,
        error,
        onCreated,
        handleClose,
        enqueueSnackbar,
        t,
    ]);

    React.useEffect(() => {
        if (!submitted || loading || !error) return;
        setSubmitted(false);
    }, [submitted, loading, error]);

    const trimmedSuffix = suffix.trim();

    const getSuffixError = (value: string) => {
        const trimmed = value.trim();
        if (!trimmed) {
            return t('settings.userManagement.create.suffixRequiredError');
        }
        if (!SUFFIX_PATTERN.test(trimmed)) {
            return t('settings.userManagement.create.invalidSuffixError');
        }
        return '';
    };

    const handleSuffixChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.toUpperCase();
        setSuffix(value);
        if (fieldError) {
            setFieldError(value.trim() ? getSuffixError(value) : '');
        }
    };

    const handleSubmit = () => {
        const validationError = getSuffixError(suffix);
        if (validationError) {
            setFieldError(validationError);
            return;
        }
        setFieldError('');
        setSubmitted(true);
        setTriedSubmit(true);
        execute();
    };

    // Enter in the suffix field submits, like clicking Submit.
    const handleSuffixKeyDown = (e: React.KeyboardEvent) => {
        if (e.key !== 'Enter' || e.nativeEvent.isComposing || loading) return;
        e.preventDefault();
        handleSubmit();
    };

    const errorMessage = React.useMemo(() => {
        if (!error) return null;
        if (error.status === 409) {
            return t('settings.userManagement.create.duplicateError');
        }
        if (error.status === 400) {
            return t('settings.userManagement.create.invalidSuffixError');
        }
        if (error.status === 403) {
            return t('settings.userManagement.create.accessDeniedError');
        }
        return t('settings.userManagement.create.genericError');
    }, [error, t]);

    const showingFailure = triedSubmit && !loading && !!errorMessage;

    return (
        <Dialog
            open={open}
            maxWidth="md"
            fullWidth
            onClose={handleClose}
            disableEscapeKeyDown={loading}
            aria-labelledby="create-service-account-title"
        >
            <DialogTitle
                id="create-service-account-title"
                onClose={handleClose}
            >
                {t(
                    showingFailure
                        ? 'settings.userManagement.create.errorTitle'
                        : 'settings.userManagement.create.title',
                )}
            </DialogTitle>
            {showingFailure ? (
                <ApiFailureDialogContent
                    errorText={errorMessage!}
                    closeDialog={handleClose}
                    closeTestId="create-service-account-failure-close"
                />
            ) : (
                <>
                    <DialogContent>
                        <TextField
                            id="service-account-suffix"
                            label={t(
                                'settings.userManagement.create.suffixLabel',
                            )}
                            value={suffix}
                            onChange={handleSuffixChange}
                            onKeyDown={handleSuffixKeyDown}
                            placeholder={
                                isMobile
                                    ? t(
                                          'settings.userManagement.create.suffixPlaceholder',
                                      )
                                    : undefined
                            }
                            inputProps={{
                                'data-testid': 'service-account-suffix-input',
                                maxLength: MAX_SUFFIX_LENGTH,
                                style: isMobile
                                    ? undefined
                                    : { paddingLeft: 0 },
                            }}
                            InputProps={{
                                startAdornment: isMobile ? undefined : (
                                    <InputAdornment
                                        position="start"
                                        style={{ marginRight: 0 }}
                                    >
                                        <span
                                            className={classes.prefixAdornment}
                                            data-testid="service-account-prefix"
                                        >
                                            {SERVICE_ACCOUNT_PREFIX}
                                        </span>
                                    </InputAdornment>
                                ),
                            }}
                            error={!!fieldError}
                            helperText={
                                <>
                                    {fieldError && (
                                        <span
                                            className={classes.suffixErrorText}
                                        >
                                            {fieldError}
                                        </span>
                                    )}
                                    <span className={classes.suffixHintText}>
                                        {trimmedSuffix ? (
                                            <>
                                                {t(
                                                    'settings.userManagement.create.suffixPreviewBefore',
                                                )}
                                                <strong
                                                    className={
                                                        classes.previewName
                                                    }
                                                >
                                                    {SERVICE_ACCOUNT_PREFIX}
                                                    <wbr />
                                                    {trimmedSuffix}
                                                </strong>
                                                {t(
                                                    'settings.userManagement.create.suffixPreviewAfter',
                                                )}
                                            </>
                                        ) : (
                                            t(
                                                'settings.userManagement.create.suffixHint',
                                            )
                                        )}
                                    </span>
                                </>
                            }
                            fullWidth
                            disabled={loading}
                        />
                    </DialogContent>
                    <DialogActions>
                        <DefaultButton
                            data-testid="create-service-account-cancel"
                            onClick={handleClose}
                            disabled={loading}
                        >
                            {t('general.cancel')}
                        </DefaultButton>
                        <PrimaryButton
                            data-testid="create-service-account-submit"
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading ? (
                                <CircularProgress
                                    aria-label={t(
                                        'settings.userManagement.create.submissionInProgress',
                                    )}
                                    size={18}
                                    data-testid="create-service-account-in-progress"
                                />
                            ) : (
                                t('settings.userManagement.create.submitButton')
                            )}
                        </PrimaryButton>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
};
